// Settings › APM link (plan step 3.4; D37; Console D, APS-319): the project's link from each
// network request to the same trace in the team's APM, an https URL with a {trace_id}
// placeholder. Owners and admins change it (audited); apps inherit it.
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, Fragment, useEffect, useId, useState } from 'react'
import { isApiError } from '../api/client'
import { orgQuery, projectsQuery, useUpdateApmLink } from '../api/queries'
import { Confirm } from '../components/Confirm'
import { PageHead, Section, Status } from '../components/d'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { urlSeams } from '../lib/format'

/** Examples shown as hints: telemetry vendors' URL shapes, never translated. */
const EXAMPLES = [
  'https://app.datadoghq.com/apm/trace/{trace_id}',
  'https://ui.honeycomb.io/<team>/environments/<env>/trace?trace_id={trace_id}',
  'https://<grafana>/explore?left={"queries":[{"query":"{trace_id}"}]}',
]

/**
 * The "What it does" example (APS-395): the trace id of W3C's own traceparent example, and the
 * URL a request with it opens, from the saved template or Datadog's shape. Shown as the mock
 * writes it: no scheme, the id cut to its first four characters.
 */
const EXAMPLE_TRACE = '4bf92f3577b34da6a3ce929d0e0e4736'
export function exampleOpens(template: string): string {
  return (template || (EXAMPLES[0] as string))
    .replace(/^https:\/\//, '')
    .replace('{trace_id}', `${EXAMPLE_TRACE.slice(0, 4)}…`)
}

/** Vendors recognised by their host; anything else is shown by its host alone. */
const VENDORS: readonly [RegExp, string][] = [
  [/(^|\.)datadoghq\.(com|eu)$|(^|\.)ddog-gov\.com$/, 'Datadog'],
  [/(^|\.)honeycomb\.io$/, 'Honeycomb'],
  [/(^|\.)grafana\.net$|grafana/, 'Grafana'],
  [/(^|\.)newrelic\.com$/, 'New Relic'],
  [/(^|\.)dynatrace\.com$/, 'Dynatrace'],
  [/(^|\.)sentry\.io$/, 'Sentry'],
  [/(^|\.)lightstep\.com$/, 'Lightstep'],
]

/** The vendor and the site (host) of a trace URL, when it parses. */
export function apmVendor(template: string): { vendor: string; site: string } | undefined {
  let host: string
  try {
    host = new URL(template.replace('{trace_id}', 'x')).hostname
  } catch {
    return undefined
  }
  const vendor = VENDORS.find(([re]) => re.test(host))?.[1]
  return { vendor: vendor ?? host, site: host }
}

export function ApmSettings({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: string
  title: string
}) {
  const { t, rich } = useI18n()
  const projects = useQuery(projectsQuery(orgId))
  const org = useQuery(orgQuery(orgId))
  const save = useUpdateApmLink(orgId, projectId)
  const project = projects.data?.find((p) => p.id === projectId)
  const current = project?.apm_trace_url_template ?? ''
  const [draft, setDraft] = useState(current)
  useEffect(() => setDraft(current), [current])
  const id = useId()
  const help = useId()
  const canEdit = org.data?.role === 'owner' || org.data?.role === 'admin'
  const linked = current ? apmVendor(current) : undefined

  function submit(e: FormEvent) {
    e.preventDefault()
    save.mutate(draft.trim() || null)
  }

  return (
    <main id="main" className="pg se-pg apm">
      <PageHead
        crumb={crumb}
        title={title}
        meta={
          project ? (
            <>
              <span translate="no">{project.name}</span>
              {` · ${t('settings.apm.meta')}`}
            </>
          ) : undefined
        }
      />
      <div className="grid2">
        <div className="sections">
          <Section
            title={t('settings.apm.title')}
            meta={linked ? <span translate="no">{linked.vendor}</span> : undefined}
            right={
              projects.data ? (
                current ? (
                  <Status s="res" label={t('settings.apm.linked')} />
                ) : (
                  <Status s="mute" label={t('settings.apm.notLinked')} />
                )
              ) : undefined
            }
          >
            {projects.isPending || org.isPending ? (
              <Loading />
            ) : projects.isError ? (
              <ErrorState error={projects.error} retry={() => projects.refetch()} />
            ) : (
              <>
                {current ? (
                  <dl className="kv" translate="no">
                    <dt>{t('settings.apm.vendor')}</dt>
                    <dd>{linked?.vendor ?? '—'}</dd>
                    <dt>{t('settings.apm.site')}</dt>
                    <dd>{linked?.site ?? '—'}</dd>
                    <dt>{t('settings.apm.label')}</dt>
                    <dd className="anywhere">{current}</dd>
                  </dl>
                ) : null}
                <form className="se-sp" style={{ paddingTop: 14 }} onSubmit={submit}>
                  <label className="lbl" htmlFor={id}>
                    {t('settings.apm.label')}
                    <input
                      id={id}
                      className="input mono"
                      style={{ maxWidth: 'none' }}
                      type="url"
                      inputMode="url"
                      translate="no"
                      maxLength={500}
                      placeholder={EXAMPLES[0]}
                      aria-describedby={help}
                      value={draft}
                      disabled={!canEdit}
                      onChange={(e) => setDraft(e.target.value)}
                    />
                  </label>
                  <div id={help} className="se-hint" style={{ minWidth: 0 }}>
                    <p>{rich('settings.apm.help')}</p>
                    {/* One URL a line, wrapped only at its own seams on a phone (APS-384, Mosaic):
                        never mid-token, nothing hidden, and copied as written (wbr adds nothing). */}
                    <ul
                      className="apm-ex mono"
                      aria-label={t('settings.apm.examples')}
                      translate="no"
                    >
                      {EXAMPLES.map((url) => (
                        <li key={url}>
                          {urlSeams(url).map((piece, i) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: the pieces of a fixed string
                            <Fragment key={i}>
                              {i > 0 ? <wbr /> : null}
                              {piece}
                            </Fragment>
                          ))}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {canEdit ? (
                    <div className="se-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="submit"
                        className="btn solid"
                        disabled={save.isPending || draft.trim() === current}
                      >
                        {t('settings.apm.save')}
                      </button>
                      {current ? (
                        <Confirm
                          label={t('settings.apm.clear')}
                          buttonClass="btn danger"
                          busy={save.isPending}
                          title={t('settings.apm.clearTitle')}
                          body={t('settings.apm.clearBody')}
                          confirm={t('settings.apm.clear')}
                          onConfirm={() => save.mutate(null)}
                        />
                      ) : null}
                    </div>
                  ) : (
                    <p className="notice">{t('settings.adminOnly')}</p>
                  )}
                  {save.isSuccess ? (
                    <p role="status" className="notice ok">
                      {save.data.apm_trace_url_template
                        ? t('settings.apm.saved')
                        : t('settings.apm.cleared')}
                    </p>
                  ) : null}
                  {save.error ? (
                    <p role="alert" className="notice error">
                      {isApiError(save.error, 400)
                        ? rich('settings.apm.invalid')
                        : isApiError(save.error, 403)
                          ? t('settings.adminOnly')
                          : t('state.failed')}
                    </p>
                  ) : null}
                </form>
              </>
            )}
          </Section>
        </div>
        <aside className="sections" aria-label={t('settings.apm.what')}>
          <Section title={t('settings.apm.what')}>
            <p className="se-p">{rich('settings.apm.body')}</p>
            <ul className="rl apm-eg" aria-label={t('settings.apm.example')}>
              <li>
                <span translate="no">
                  <code>POST /v1/orders</code> <span className="pill crash">500</span>
                </span>
                <span translate="no">{`trace ${EXAMPLE_TRACE.slice(0, 4)}`}</span>
              </li>
              <li>
                <span className="dim">{t('settings.apm.opens')}</span>
                <span className="apm-eg-url" translate="no">
                  {urlSeams(exampleOpens(current)).map((piece, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: the pieces of one string
                    <Fragment key={i}>
                      {i > 0 ? <wbr /> : null}
                      {piece}
                    </Fragment>
                  ))}
                </span>
              </li>
            </ul>
            <p className="se-hint" style={{ paddingTop: 10 }}>
              {t('settings.apm.ownersOnly')}
            </p>
          </Section>
        </aside>
      </div>
    </main>
  )
}
